import { useCallback, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFloppyDisk,
  faPlus,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { createPresentation } from "../redux/features/presentations/presentationsSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { fetchBrands } from "../redux/features/brands/brandsSlice";
import AnimationEditor from "../components/AnimateTab/AnimationEditor";
import { Film } from "lucide-react";
import { appendNestedFormData } from "../utils/appendNestedFormData";
import { resolveAnimationDataForUpload } from "../utils/animationDataUtils";
import CreateLeftPanel from "../components/CreatePresentation/CreateLeftPanel";
import CreateMiddlePanel from "../components/CreatePresentation/CreateMiddlePanel";
import CreateRightPanel from "../components/CreatePresentation/CreateRightPanel";

export default function CreatePresentation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createPresentationLoading } = useSelector(
    (state) => state.presentation,
  );
  const { brands } = useSelector((state) => state.brands);
  const [presentationTitle, setPresentationTitle] = useState("");
  const [slides, setSlides] = useState([]);
  const [activeSlide, setActiveSlide] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("interactive");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [activeQuestionTab, setActiveQuestionTab] = useState("poll");
  const [brandId, setBrandId] = useState("");
  const [isAnimateMode, setIsAnimateMode] = useState(false);
  const [animatedSlides, setAnimatedSlides] = useState({});
  const [slidesAnimationData, setSlidesAnimationData] = useState({});
  const [pendingAnimationAssets, setPendingAnimationAssets] = useState({});

  const isCurrentSlideAnimated =
    activeSlide !== null && !!animatedSlides[activeSlide];

  const activeSlideImageUrl =
    activeSlide !== null && slides[activeSlide]?.type === "image"
      ? slides[activeSlide]?.image instanceof File
        ? URL.createObjectURL(slides[activeSlide].image)
        : slides[activeSlide]?.imageUrl || null
      : null;

  const handleSaveAnimation = (data, pendingFiles = {}) => {
    setSlidesAnimationData((prev) => ({ ...prev, [activeSlide]: data }));
    setAnimatedSlides((prev) => ({ ...prev, [activeSlide]: true }));
    if (pendingFiles && Object.keys(pendingFiles).length > 0) {
      setPendingAnimationAssets((prev) => ({ ...prev, ...pendingFiles }));
    }
    // No API call here — everything rides on the single create-presentation request
  };

  const [presentationId] = useState(() => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  });

  useEffect(() => {
    dispatch(fetchBrands());
  }, [dispatch]);

  const generateRandomId = () => {
    return Math.random().toString(36).substring(2);
  };

  const handleAddSlide = (data) => {
    const newSlide = {
      id: generateRandomId(),
      presentationId: presentationId,
      is_active: true,
      type: "question",
      question_type: data.type,
      title:
        data.type === "poll"
          ? "Poll Question:"
          : data.type === "multiple"
            ? "Multiple Question:"
            : "Text Question:",
      question: data.question,
      choices: data.choices || [],
    };

    setSlides((prev) => [...prev, newSlide]);
    setActiveSlide(slides.length);
  };

  const getLinks = () => {
    if (!presentationId) return { speaker: "", attendee: "" };
    return {
      speaker: `${process.env.REACT_APP_URL}/${presentationId}`,
      attendee: `${process.env.REACT_APP_URL}/${presentationId}/attendee`,
    };
  };

  const handleCreateExport = async () => {
    try {
      const formData = new FormData();
      const { speaker } = getLinks();

      // Presentation fields
      formData.append("presentation[title]", presentationTitle);
      formData.append("presentation[presentation_url]", speaker);
      formData.append("presentation[brand_id]", brandId);

      // Slides
      slides.forEach((slide, i) => {
        formData.append(`slides[${i}][order]`, i + 1);
        formData.append(`slides[${i}][is_active]`, slide.is_active ? 1 : 0);
        formData.append(`slides[${i}][type]`, slide.type);
        formData.append(`slides[${i}][title]`, slide.title || "");

        // ✅ Handle Question Slide
        if (slide.type === "question") {
          formData.append(
            `slides[${i}][question][question_type]`,
            slide.question_type,
          );
          formData.append(
            `slides[${i}][question][question_text]`,
            slide.question || "",
          );

          // ✅ Append choices (only for poll/multiple)
          if (
            (slide.question_type === "multiple" ||
              slide.question_type === "poll") &&
            Array.isArray(slide.choices)
          ) {
            slide.choices.forEach((choice, index) => {
              formData.append(
                `slides[${i}][question][choices][${index}][choice_text]`,
                typeof choice === "string" ? choice : choice.choice_text,
              );
              formData.append(
                `slides[${i}][question][choices][${index}][order]`,
                choice.order || index + 1,
              );
            });
          }
        }

        // ✅ Handle Image
        if (slide.type === "image" && slide.image) {
          formData.append(`slides[${i}][image_title]`, slide.image_title || "");
          formData.append(`slides[${i}][image]`, slide.image);
        }

        if (animatedSlides[i] && slidesAnimationData[i]) {
          formData.append(`slides[${i}][with_animation]`, 1);
          appendNestedFormData(
            formData,
            `slides[${i}][animation_data]`,
            resolveAnimationDataForUpload(slidesAnimationData[i]),
          );
        }
      });

      // New animation asset files (images dropped in the animation editor, keyed by temp placeholder)
      Object.entries(pendingAnimationAssets).forEach(([tempKey, file]) => {
        formData.append(`assets[${tempKey}]`, file);
      });

      await dispatch(createPresentation(formData)).unwrap();
      toast.success("Presentation created successfully!");
      navigate("/presentations");
    } catch (err) {
      console.error("Error creating presentation:", err);
      toast.error(err.message || "Failed to create presentation.");
    }
  };

  return (
    <div className="min-h-screen bg-lightPurple/5">
      <Navbar />
      <div className="flex justify-between items-center px-8 mt-6">
        <div className="flex gap-4">
          {/* Back button placeholder alignment */}
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <div className="w-full max-w-3xl">
          <h2 className="text-3xl font-semibold text-gray-800 mb-8 text-center">
            Create Presentation
          </h2>

          <div className="mb-4">
            <label className="block text-lg font-medium mb-2">
              Presentation Title
            </label>
            <input
              type="text"
              value={presentationTitle}
              onChange={(e) => setPresentationTitle(e.target.value)}
              placeholder="Enter presentation title"
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lightPurple capitalize transition"
            />
          </div>

          {/* Brand Select */}
          <div className="mb-2">
            <label className="block text-lg font-medium mb-2">Brand</label>
            <select
              name="brand_id"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-lightPurple transition"
            >
              <option value="" hidden>
                Select Brand
              </option>
              {brands?.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isAnimateMode ? (
        <div className="px-8 pb-12">
          <div className="mb-4">
            <button
              onClick={() => setIsAnimateMode(false)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-purpleColor transition font-medium"
            >
              ← Back to Slide Editor
            </button>
          </div>
          <AnimationEditor
            slideImageUrl={activeSlideImageUrl}
            onSave={handleSaveAnimation}
            initialData={slidesAnimationData[activeSlide] || null}
            slideContent={
              slides[activeSlide]?.type === "question"
                ? {
                    question: slides[activeSlide]?.question || "",
                    questionType: slides[activeSlide]?.question_type || "poll",
                    choices: (slides[activeSlide]?.choices || []).map(
                      (choice) =>
                        typeof choice === "object"
                          ? choice.choice_text
                          : choice,
                    ),
                  }
                : null
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-4 p-8">
          {/* Left Panel: Slides List */}
          <CreateLeftPanel
            slides={slides}
            activeSlide={activeSlide}
            setActiveSlide={setActiveSlide}
            isDragging={isDragging}
            isProcessing={isProcessing}
            slidesAnimationData={slidesAnimationData}
            presentationId={presentationId}
            setSlides={setSlides}
            setSlidesAnimationData={setSlidesAnimationData}
            setAnimatedSlides={setAnimatedSlides}
            setIsProcessing={setIsProcessing}
            setIsDragging={setIsDragging}
            setDraggedIndex={setDraggedIndex}
            draggedIndex={draggedIndex}
            generateRandomId={generateRandomId}
          />
          {/* Middle Panel: Slide Preview */}
          <CreateMiddlePanel
            slides={slides}
            activeSlide={activeSlide}
            slidesAnimationData={slidesAnimationData}
          />
          {/* Right Panel: Properties Editor */}
          <CreateRightPanel
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            activeQuestionTab={activeQuestionTab}
            setActiveQuestionTab={setActiveQuestionTab}
            handleAddSlide={handleAddSlide}
            activeSlide={activeSlide}
            slides={slides}
            setSlides={setSlides}
            animatedSlides={animatedSlides}
            setAnimatedSlides={setAnimatedSlides}
            isCurrentSlideAnimated={isCurrentSlideAnimated}
            setIsAnimateMode={setIsAnimateMode}
            slidesAnimationData={slidesAnimationData}
            setSlidesAnimationData={setSlidesAnimationData}
            handleCreateExport={handleCreateExport}
            createPresentationLoading={createPresentationLoading}
            presentationId={presentationId}
            getLinks={getLinks}
          />
        </div>
      )}
      <Footer />
    </div>
  );
}
